// src/components/VideoExplanation.tsx
'use client'

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import Link from 'next/link'
import {
  getVideosForTopic,
  getYouTubeId,
  getYouTubeThumbnail,
  OUR_SOURCE,
  type TopicVideo,
} from '@/lib/content/topic-videos'
import { videoByYouTubeId, videoPath } from '@/lib/content/video-catalog'

/** Single video row inside the collapsible panel */
function VideoItem({ video, onPlay }: { video: TopicVideo; onPlay: (video: TopicVideo) => void }) {
  const thumbnail = getYouTubeThumbnail(video.url)

  return (
    <button
      type="button"
      className="flex w-full items-center gap-3 rounded-lg border border-border/60 bg-muted/40 p-2 text-left transition hover:bg-muted/80"
      onClick={() => onPlay(video)}
    >
      {thumbnail && (
        <img
          src={thumbnail}
          alt={video.title}
          className={
            video.vertical
              ? 'h-16 w-9 shrink-0 rounded object-cover'
              : 'h-14 w-24 shrink-0 rounded object-cover sm:h-16 sm:w-28'
          }
        />
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{video.title}</p>
        <div className="mt-1 flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{video.duration}</span>
          <Badge variant="outline" className="border-slate-600 text-[10px] text-muted-foreground">
            {video.source}
          </Badge>
        </div>
      </div>
    </button>
  )
}

/** Embedded YouTube player */
function EmbeddedPlayer({ video, onClose }: { video: TopicVideo; onClose: () => void }) {
  const videoId = getYouTubeId(video.url)
  if (!videoId) return null
  const ours = videoByYouTubeId(videoId)

  return (
    <div className={video.vertical ? 'mx-auto mt-3 max-w-[300px] space-y-2' : 'mt-3 space-y-2'}>
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-foreground">{video.title}</p>
        <Button
          variant="ghost"
          size="sm"
          className="text-xs text-muted-foreground hover:text-foreground"
          onClick={onClose}
        >
          Close player
        </Button>
      </div>
      <div
        className="relative w-full overflow-hidden rounded-lg"
        /* 16:9 is 56.25%; a 9:16 Short needs 177.78% or it renders as a sliver between bars */
        style={{ paddingBottom: video.vertical ? '177.78%' : '56.25%' }}
      >
        <iframe
          className="absolute inset-0 h-full w-full rounded-lg"
          src={`https://www.youtube-nocookie.com/embed/${videoId}?rel=0`}
          title={video.title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
      {ours ? (
        <Link
          href={videoPath(ours)}
          className="inline-block text-xs text-brand-ink hover:text-brand-ink hover:underline"
        >
          Worked steps, lesson and practice for this →
        </Link>
      ) : (
        <a
          href={video.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block text-xs text-brand-ink hover:text-brand-ink hover:underline"
        >
          Open in YouTube
        </a>
      )}
    </div>
  )
}

/**
 * Collapsible video explanation panel.
 *
 * Pass either `topic` (to auto-look up from the library)
 * or `videoUrl` + `videoTitle` (for a question-specific video).
 */
export default function VideoExplanation({
  topic,
  videoUrl,
  videoTitle,
  defaultOpen = false,
}: {
  topic?: string
  videoUrl?: string | null
  videoTitle?: string | null
  defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)
  const [activeVideo, setActiveVideo] = useState<TopicVideo | null>(null)

  // Build the video list: question-specific first, then topic library
  const topicEntry = topic ? getVideosForTopic(topic) : undefined
  const videos: TopicVideo[] = []

  if (videoUrl) {
    videos.push({
      title: videoTitle || 'Video Explanation',
      url: videoUrl,
      duration: '',
      source: 'Question Video',
    })
  }

  if (topicEntry) {
    for (const v of topicEntry.videos) {
      // Avoid duplicates if the question video is the same as a topic video
      if (!videos.some((existing) => existing.url === v.url)) {
        videos.push(v)
      }
    }
  }

  if (videos.length === 0) return null
  const hasOurs = videos.some((v) => v.source === OUR_SOURCE)

  return (
    <div className="mt-2">
      <Button
        variant="outline"
        size="sm"
        className="border-brand/50 text-brand-ink hover:bg-brand/15 dark:bg-brand/40 hover:text-brand-ink"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? 'Hide videos' : hasOurs ? 'Watch the 2-minute version' : 'Watch video explanation'}
        <span className="ml-1.5 text-xs text-brand-ink">({videos.length})</span>
      </Button>

      {open && (
        <Card className="mt-2 border-brand/40 bg-card/80 p-3">
          {activeVideo ? (
            <EmbeddedPlayer video={activeVideo} onClose={() => setActiveVideo(null)} />
          ) : (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-brand-ink">
                {topicEntry ? `${topicEntry.topic} Videos` : 'Related Videos'}
              </p>
              {videos.map((v) => (
                <VideoItem key={v.url} video={v} onPlay={setActiveVideo} />
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  )
}
